<template>
    <Portal to="modal">
        <div
            v-if="showKeywordDefinition && keywordDefinitionParams"
            :style="{
                position: 'absolute',
                top: `${computedQuestionContainerRect?.top}px`,
                height: `${computedQuestionContainerRect?.height}px`,
                width: '100%',
                overflow: 'hidden'
            }"
        >
            <KeywordDefinition
                :keywordDOMElement="keywordDefinitionParams.keywordDOMElement"
                :keywordClickLocation="keywordDefinitionParams.keywordClickLocation"
                :keyword="keywordDefinitionParams.keyword"
                :definition="keywordDefinitionParams.definition"
                :questionContainer="keywordDefinitionParams.questionContainer"
                :serial="keywordDefinitionParams.serial"
                :keywordLocation="keywordDefinitionParams.location"
                :adjustBaseTopCoordinate="questionContainerRect?.top"
                :isMobile="true"
                @close="closeKeywordDefinition"
            />
        </div>
    </Portal>
    <div v-dark class="qotd-container">
        <BonusQuestionSurvey
            v-if="showSurvey"
            :variant="bonusQuestionVariantValue === 'short-answer-question'
                ? 'shortAnswerQuestion'
                : 'explanationAnswerQuestion'
            "
            :bonusQText="bonusQuestionUserText"
            @submit="submitSurvey"
            @close="closeSurvey"
        />
        <Icon
            v-if="!question || isLoading"
            v-dark
            class="qotd-container__loading"
            type="loading2"
            :is-dark-mode="isDarkMode"
        />
        <BonusQuestionExperiment
            v-else-if="examMetadata && showBonusQuestion && bonusQuestion"
            :examGuid="examMetadata.examGuid"
            :question="bonusQuestion"
            :is-dark-mode="isDarkMode"
            :breakpoint-override="'black-bear'"
            @skip="skipBonusQuestion"
            @userAnswer="updateUserShortAnswer"
            @showSurvey="setShowSurveyTrue"
        />
        <Question
            v-else-if="question && examMetadata && !showBonusQuestion"
            ref="qotdContainer"
            class="qotd-container__question"
            :class="{ 'qotd-container__question--correct': isCorrect }"
            :question="qotdModule.getters.getRenderableQotDQuestion(question)"
            :previous-choices="previousChoicesTyped"
            :previous-matrix-choices="previousMatrixChoicesTyped"
            :previous-build-list-choices="previousBuildListChoicesTyped"
            :previous-multi-part-multiple-choice-choices="previousChoicesTyped"
            :initial-show-answers="showAnswers"
            :show-next-question="false"
            :show-check-answer="true"
            :global-metrics="!isCommunityStatsDisabled && showCommunityStats ? globalMetric : null"
            :quiz-mode="'qotd'"
            :is-dark-mode="isDarkMode"
            :image-url-prefix="`${s3Url || ''}/${question.compositeKey.toUpperCase()}/`"
            :hide-references="examMetadata.hideReferences"
            :keyword-definitions="keywordDefinitions"
            :allow-keyboard-shortcuts="false"
            :auto-focus-prompt="null"
            @keywordClick="keywordClicked"
            @checkAnswer="submitQotD"
            @selectedChoices="selectChoices"
        >
            <template #closeIcon>
                <!-- Hide the close icon -->
                <div />
            </template>
            <template #promptExperiment>
                <BonusQuestionBanner
                    v-if="showUserExplanationField"
                    class="qotd-container__bonus-question-banner"
                    variant="explanation-answer-question"
                    :is-dark-mode="isDarkMode"
                />
            </template>
            <template #actionExperiment>
                <BonusQuestionTextarea
                    v-if="showUserExplanationField"
                    class="qotd-container__bonus-question-explanation-textarea"
                    v-model="userExplanation"
                    label="Why is this the correct answer?"
                    height="134px"
                    :disabled="!selectedChoices.length || showAnswers"
                    :is-dark-mode="isDarkMode"
                />
            </template>
        </Question>
        <div class="qotd-container__toolbar">
            <div
                class="qotd-container__people-icon"
                :class="{
                    'qotd-container__people-icon--active': showCommunityStats,
                    'qotd-container__people-icon--disabled': isCommunityStatsDisabled,
                }"
            >
                <GlobalMetricsToggle
                    :show-global-metrics="showCommunityStats"
                    :custom-tooltip-styles="!isDarkMode
                        ? {
                            backgroundColor: 'rgba(31, 53, 69, 0.95)',
                            color: brandColors.white,
                            marginTop: '-9px'
                        }
                        : {
                            backgroundColor: brandColors.white,
                            color: brandColors.brandBlack,
                            marginTop: '-9px'
                        }"
                    :disabled="isCommunityStatsDisabled"
                    :is-dark-mode="isDarkMode"
                    @toggleGlobalMetrics="toggleCommunityStats"
                />
            </div>
            <div
                class="qotd-container__flag-toggle"
                :class="{
                    'qotd-container__flag-toggle--disabled': isFlagDisabled,
                }"
                :tabindex="isFlagDisabled ? -1 : 0"
                role="checkbox"
                :aria-checked="isFlagged ? 'true' : 'false'"
                aria-label="Flag Question"
                @mouseover="setShowFlagTooltipTrue"
                @mouseleave="setShowFlagTooltipFalse"
                @click="toggleFlagStatus"
                @keydown.enter.stop.prevent="toggleFlagStatus"
                @mousedown.prevent
                @focus="setShowFlagTooltipTrue"
                @blur="setShowFlagTooltipFalse"
            >
                <Tooltip
                    v-if="showFlagTooltip"
                    :key="`flag-tooltip_${isFlagged}`"
                    :is-dark-mode="isDarkMode"
                >
                    {{ isFlagged ? 'Unflag' : 'Flag' }}
                </Tooltip>
                <Icon
                    class="qotd-container__flag-icon"
                    :class="{
                        'qotd-container__flag-icon--active': isFlagged,
                    }"
                    :type="isFlagged ? 'flag-filled' : 'flag'"
                />
            </div>
            <div
                class="qotd-container__share-icon"
                :class="{
                    'qotd-container__share-icon--disabled': isShareDisabled,
                }"
            >
                <Tooltip v-if="!showLinkCopiedTooltip && showShareTooltip" :is-dark-mode="isDarkMode">
                    Share Question Link
                </Tooltip>
                <Tooltip
                    v-if="showLinkCopiedTooltip"
                    :styles="{
                        backgroundColor: brandColors.green,
                        color: brandColors.brandBlack,
                    }"
                >
                    <span role="alert">Link Copied!</span>
                </Tooltip>
                <Icon
                    type="share"
                    :tabindex="isShareDisabled ? -1 : 0"
                    role="button"
                    aria-label="Share Question of the Day"
                    @mouseover="mouseOverShareIcon"
                    @mouseleave="setShowShareTooltipFalse"
                    @click="copyShareLink"
                    @keydown.enter="copyShareLink"
                    @mousedown.prevent
                    @focus="mouseOverShareIcon"
                    @blur="setShowShareTooltipFalse"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import type { Study } from '@pocketprep/types'
import { qotdModule } from '@/store/qotd/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { copyText } from '@/utils'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { userModule } from '@/store/user/module'
import { analyticsModule } from '@/store/analytics/module'
/* Start Bonus Question Experiment imports */
import BonusQuestionExperiment from '@/components/BonusQuestionExperiment/BonusQuestionExperiment.vue'
import BonusQuestionBanner from '@/components/BonusQuestionExperiment/BonusQuestionBanner.vue'
import BonusQuestionSurvey from '@/components/BonusQuestionExperiment/BonusQuestionSurvey.vue'
import BonusQuestionTextarea from '@/components/BonusQuestionExperiment/BonusQuestionTextarea.vue'
import { toastModule } from '@/store/toast/module'
/* End Bonus Question Experiment imports */
import { keywordsModule } from '@/store/keywords/module'
import KeywordDefinition from '../KeywordDefinitions/KeywordDefinition.vue'

const {
    Icon,
    Tooltip,
    Question,
    GlobalMetricsToggle,
} = UIKit

// UIKit Question component ChoiceKey props
type TChoiceKey = `${'a' | 'd'}${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`
type TMatrixChoiceKey = `${'a' | 'd'}${1 | 2 | 3}_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`
type TBuildListChoiceKey = `a${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`

const qotdContainer = ref<InstanceType<typeof Question> | null>(null)
const isLoading = ref(true)
const isSubmitLoading = ref(false)
const showFlagTooltip = ref(false)
const showShareTooltip = ref(false)
const showLinkCopiedTooltip = ref(false)
const showCommunityStats = ref(false)
const isCommunityStatsDisabled = ref(true)
const isFlagged = ref(false)
const isTogglingFlag = ref(false)
const brandColors = BrandColors
const isCorrect = ref(false)
const clientVersion = import.meta.env.VUE_APP_VERSION
/* Start Bonus Question Experiment properties */
const disableExperiment = ref(false)
const showSurvey = ref(false)
const selectedChoices = ref<Study.Cloud.TChoiceKey[]>([])
const bonusQuestionVariantValue = ref<'short-answer-question' | 'explanation-answer-question' | null>(null)
const userShortAnswer = ref('')
const userExplanation = ref('')
/* End Bonus Question Experiment properties */
const showKeywordDefinition = ref(false)
const visibleKeywordDefinition = ref<{
    keyword: string
    definition: string
    location: string
} | null>(null)
const keywordDOMElement = ref<HTMLElement | null>(null)
const keywordClickLocation = ref<{ x: number; y: number } | null>(null)
const questionContainerRect = ref<DOMRect | null>(null)

const keywordDefinitionParams = computed(() => {
    const el = keywordDOMElement.value
    const loc = keywordClickLocation.value
    const container = questionContainer.value
    const visible = visibleKeywordDefinition.value
    const serial = question.value?.serial
    if (!el || !loc || !container || !visible || serial === undefined) return null
    return {
        keywordDOMElement: el,
        keywordClickLocation: loc,
        questionContainer: container,
        keyword: visible.keyword,
        definition: visible.definition,
        location: visible.location,
        serial,
    }
})

const keywordDefinitions = computed(() => {
    const questionSerial = question.value?.serial
    const questionKeywords = questionSerial && keywordsModule.getters.getKeywordDefinitions().get(questionSerial)

    if (
        !showAnswers.value
        || !questionKeywords
    ) {
        return []
    }

    return Array.from(questionKeywords, ([ keyword, definition ]) => ({
        keyword,
        definition,
    }))
})

const questionContainer = computed(() => {
    const questionContainerRef = qotdContainer.value
    if (!questionContainerRef || !('$el' in questionContainerRef)) {
        return null
    }
    return (questionContainerRef as { $el: HTMLElement }).$el
})

const computedQuestionContainerRect = computed(() => {
    return questionContainerRect.value
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const sharing = computed(() => {
    if (!bundle.value || !examMetadata.value) {
        return undefined
    }

    return qotdModule.getters.getQotDSharingInfo({
        bundleId: bundle.value.objectId,
        examGuid: examMetadata.value.examGuid,
        version: examMetadata.value.version,
        userId: userModule.state.user?.objectId || '',
        examName: examMetadata.value.nativeAppName,
    })
})

const bundle = computed(() => {
    return bundleModule.getters.getCurrentBundle()
})

const examMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const showAnswers = computed(() => {
    return !!quiz.value
})

const previousChoices = computed(() => {
    return quiz.value?.answers[0]?.selectedChoices || []
})

// Question component expects literal union types; cast from string[] for type compatibility
const previousChoicesTyped = computed(() => {
    // non matrix and build list questions
    return previousChoices.value as TChoiceKey[]
})

const previousMatrixChoicesTyped = computed(() => {
    return previousChoices.value as TMatrixChoiceKey[]
})

const previousBuildListChoicesTyped = computed(() => {
    return previousChoices.value as TBuildListChoiceKey[]
})

const question = computed(() => {
    return qotdModule.getters.getQotDQuestion()
})

const quiz = computed(() => {
    return qotdModule.getters.getQotDQuiz()
})

const globalMetric = computed(() => {
    return !isCommunityStatsDisabled.value && showCommunityStats.value ? qotdModule.getters.getQotDMetric() : null
})

const flaggedQuestions = computed(() => {
    return userExamMetadataModule.getters.getFlaggedQuestions()
})

const isFlaggedInStore = computed(() => {
    const questionSerial = question.value?.serial
    return !!(questionSerial && flaggedQuestions.value.includes(questionSerial))
})

const s3Url = computed(() => {
    return import.meta.env.VUE_APP_S3_URL
})

/* Start Bonus Question Experiment Getters */
const bonusQuestion = computed(() => {
    const examGuid = examMetadata.value?.examGuid
    if (!examGuid) {
        return null
    }

    const examGuidQuestionLibString = import.meta.env.VUE_APP_BONUS_QUESTION_EXPERIMENT_EXAM_GUID_QUESTION_LIB
    const examGuidQuestionLib = JSON.parse(examGuidQuestionLibString || '{}') as {
        [examGuid: string]: {
            prompt: string
            answer: string
            explanation: string
            reference: string
        }
    }
    return examGuidQuestionLib[examGuid]
})

const isBonusQuestionExperimentActive = computed(() => {
    // Check if current date is within experiment date range
    const currentDate = new Date()
    const dateRangeString = import.meta.env.VUE_APP_BONUS_QUESTION_EXPERIMENT_DATE_RANGE
    const dateRange = JSON.parse(dateRangeString || '[]') as [string, string]
    const [ startDate, endDate ] = dateRange.map(dateString => new Date(dateString))
    if (currentDate < startDate || currentDate > endDate) {
        return false
    }

    // Check if we have a current exam
    if (!examMetadata.value?.examGuid) {
        return false
    }

    // Check if we have a bonus experiment question for the current exam
    if (!bonusQuestion.value) {
        return false
    }

    return true
})

const showBonusQuestion = computed(() => {
    if (disableExperiment.value) {
        return false
    }
    if (!bonusQuestion.value) {
        return false
    }

    return bonusQuestionVariantValue.value === 'short-answer-question'
})

const showUserExplanationField = computed(() => {
    if (disableExperiment.value) {
        return false
    }

    return bonusQuestionVariantValue.value === 'explanation-answer-question'
})

const isFlagDisabled = computed(() => {
    return showBonusQuestion.value
})

const isShareDisabled = computed(() => {
    return showBonusQuestion.value
})

const bonusQuestionUserText = computed(() => {
    return bonusQuestionVariantValue.value === 'short-answer-question'
        ? userShortAnswer.value
        : userExplanation.value
})
/* End Bonus Question Experiment Getters */

onMounted(async () => {
    await Promise.all([
        qotdModule.actions.fetchCurrentQotDMetric(),
        examMetadataModule.actions.fetchExamMetadata(),
        bundleModule.actions.fetchBundles(),
        userModule.actions.fetchUserData(),
    ])

    // Setup Bonus Question Experiment
    if (isBonusQuestionExperimentActive.value) {
        const variant = await analyticsModule.actions.amplitudeFetchVariant('bonus-question-experiment')
        const variantValue = variant?.value as 'short-answer-question' | 'explanation-answer-question'
        bonusQuestionVariantValue.value = variantValue || null
        initializeBonusQuestionExperiment()
    }

    if (quiz.value && !showBonusQuestion.value) {
        isCommunityStatsDisabled.value = false
    }
    const questionSerial = question.value?.serial
    if (questionSerial && flaggedQuestions.value.includes(questionSerial)) {
        isFlagged.value = true
    }

    const studyContainer = document.getElementsByClassName('study__body')[0]
    studyContainer.addEventListener('scroll', updateQuestionContainerRect)

    // fetch keyword data
    const serial = question.value?.serial
    if (serial) {
        await keywordsModule.actions.fetchKeywordDefinitions([ serial ])
    }

    isLoading.value = false
})

onBeforeUnmount(() => {
    const studyContainer = document.getElementsByClassName('study__body')[0]
    studyContainer.removeEventListener('scroll', updateQuestionContainerRect)
})

const updateQuestionContainerRect = () => {
    questionContainerRect.value = questionContainer.value?.getBoundingClientRect() || null
}

const keywordClicked = (params?: {
    keyword: string
    location: string
    clickLocation: { x: number; y: number }
    target: HTMLElement
}) => {
    showKeywordDefinition.value = false
    if (!params?.keyword) {
        return
    }

    updateQuestionContainerRect()

    const questionKeywords = question.value
        && keywordsModule.getters.getKeywordDefinitions().get(question.value.serial)
    const definition = questionKeywords?.get(params.keyword.toLowerCase())

    if (!definition) {
        return
    }

    analyticsModule.actions.amplitudeTrack('Keyword Definition Viewed', {
        examGuid: examMetadata.value?.examGuid,
        definitionKeyword: params.keyword,
        serial: question.value?.serial,
        location: params.location,
        quizMode: -1,
        studyMode: 'Active Quiz',
    })

    visibleKeywordDefinition.value = {
        keyword: params.keyword,
        definition,
        location: params.location,
    }
    keywordDOMElement.value = params.target
    keywordClickLocation.value = params.clickLocation

    nextTick(() => {
        showKeywordDefinition.value = true
    })
}

const submitQotD = async (answer: Study.Cloud.IQuizAnswer) => {
    // Prevent duplicate submission
    if (isSubmitLoading.value) {
        return
    }
    isSubmitLoading.value = true
    isCommunityStatsDisabled.value = false
    if (answer.isCorrect) {
        isCorrect.value = answer.isCorrect
    }
    // Store the bonus question experiment answer before submitting the quiz
    if (showUserExplanationField.value) {
        const examGuid = examMetadata.value?.examGuid
        localStorage.setItem(`bonusQuestionExperiment_${examGuid}_explanationAnswer`, userExplanation.value)
        analyticsModule.actions.amplitudeTrack('Bonus Question Answered', {
            bonusQuestionText: userExplanation.value,
            bonusQuestionVariation: 'explanationAnswerQuestion',
        })
    }
    await qotdModule.actions.recordQotD(answer)
    isSubmitLoading.value = false

    // Show the bonus question experiment survey 3 seconds after submitting the quiz
    if (showUserExplanationField.value) {
        setTimeout(() => {
            showSurvey.value = true
        }, 3000)
    }
}

const toggleCommunityStats = () => {
    if (!isCommunityStatsDisabled.value) {
        showCommunityStats.value = !showCommunityStats.value
    }
}

const toggleFlagStatus = async () => {
    // Prevent if already processing toggleQuestionFlag call
    if (isTogglingFlag.value) {
        return
    }

    isTogglingFlag.value = true
    if (isFlagDisabled.value) {
        return
    }

    const serial = question.value?.serial
    if (!serial) {
        throw new Error('Unable to flag question without a serial')
    }
    isFlagged.value = !isFlagged.value

    try {
        await userExamMetadataModule.actions.toggleQuestionFlag(serial)
    } catch (e) {
        // noop
    } finally {
        isFlagged.value = !!(serial && flaggedQuestions.value.includes(serial))
        isTogglingFlag.value = false
    }
}

const mouseOverShareIcon = () => {
    if (isShareDisabled.value) {
        return
    }

    if (!showLinkCopiedTooltip.value) {
        showShareTooltip.value = true
    }
}

const copyShareLink = () => {
    if (!sharing.value || isShareDisabled.value) {
        return
    }

    copyText(sharing.value.url)
    showShareTooltip.value = false
    nextTick(() => {
        showLinkCopiedTooltip.value = true
        setTimeout(() => {
            showLinkCopiedTooltip.value = false
        }, 2000)
    })

    analyticsModule.actions.amplitudeTrack('QotD Shared', {
        bundleId: bundle.value?.objectId,
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion: clientVersion,
        examGuid: examMetadata.value?.examGuid,
        organizationId: userModule.getters.getCurrentOrganizationId() || undefined,
        userDomain: userModule.getters.getUserDomain() || undefined,
    })
}

/* Start Bonus Question Experiment Methods */
const selectChoices = (answer: Study.Cloud.IQuizAnswer) => {
    selectedChoices.value = answer.selectedChoices
}

const initializeBonusQuestionExperiment = () => {
    const isBonusQuestionComplete = localStorage.getItem('bonusQuestionExperimentComplete')
    if (bonusQuestionVariantValue.value === 'explanation-answer-question') {
        const examGuid = examMetadata.value?.examGuid
        const localStorageAnswer = localStorage.getItem(`bonusQuestionExperiment_${examGuid}_explanationAnswer`)
        if (localStorageAnswer) {
            userExplanation.value = localStorageAnswer
            disableExperiment.value = false  // Enable to show user their previous answer
        } else if (localStorageAnswer === null && quiz.value) {
            disableExperiment.value = true   // Disable if qotd answered outside of experiment
        } else if (isBonusQuestionComplete) {
            disableExperiment.value = true   // Disable if already participated in experiment
        } else {
            disableExperiment.value = false  // Enable if qotd unanswered and experiment still incomplete
        }
    } else if (bonusQuestionVariantValue.value === 'short-answer-question') {
        const examGuid = examMetadata.value?.examGuid
        const localStorageAnswer = localStorage.getItem(`bonusQuestionExperiment_${examGuid}_shortAnswer`)
        if (localStorageAnswer === null && quiz.value) {
            disableExperiment.value = true   // Disable if qotd answered outside of experiment
        } else if (isBonusQuestionComplete) {
            disableExperiment.value = true   // Disable if already participated in experiment
        } else {
            disableExperiment.value = false  // Enable if qotd unanswered and experiment still incomplete
        }
    }
}

const skipBonusQuestion = () => {
    localStorage.setItem('bonusQuestionExperimentComplete', 'true')
    disableExperiment.value = true
    isCommunityStatsDisabled.value = !quiz.value
}

const updateUserShortAnswer = (userAnswer: string) => {
    userShortAnswer.value = userAnswer
}

const submitSurvey = () => {
    showSurvey.value = false
    toastModule.actions.displayToast({
        title: 'Thank you for your feedback!',
    })
    completeBonusQuestionExperiment()
}

const closeSurvey = () => {
    showSurvey.value = false
    completeBonusQuestionExperiment()
}

const completeBonusQuestionExperiment = () => {
    localStorage.setItem('bonusQuestionExperimentComplete', 'true')
    if (bonusQuestionVariantValue.value === 'short-answer-question') {
        disableExperiment.value = true
    }
    isCommunityStatsDisabled.value = !quiz.value
}
/* End Bonus Question Experiment Methods */

// This helps update the component's flagged state when switching exams
watch(isFlaggedInStore, (isFlaggedVal) => {
    isFlagged.value = isFlaggedVal
})

// This helps enable/disable the community stats button when switching exams
watch(quiz, (quizVal) => {
    showCommunityStats.value = false
    isCommunityStatsDisabled.value = !quizVal || showBonusQuestion.value
    initializeBonusQuestionExperiment()
})

// Wrapper functions for template ref assignments (refs must be set via .value in script)
const closeKeywordDefinition = () => {
    showKeywordDefinition.value = false
}
const setShowSurveyTrue = () => {
    showSurvey.value = true
}
const setShowFlagTooltipTrue = () => {
    if (!isFlagDisabled.value) {
        showFlagTooltip.value = true
    }
}
const setShowFlagTooltipFalse = () => {
    if (!isFlagDisabled.value) {
        showFlagTooltip.value = false
    }
}
const setShowShareTooltipFalse = () => {
    if (!isShareDisabled.value) {
        showShareTooltip.value = false
    }
}
</script>

<style lang="scss" scoped>
.uikit-question {
    &__context {
        margin-bottom: 28px;
    }

    &__context-icon {
        height: 19px;
    }
}

:deep(.uikit-question-choices-container__choice-container--correct p) {
    padding-right: 18px;
}

.qotd-container {
    box-sizing: border-box;
    width: 387px;
    height: 647px;
    border: 6px solid $white;
    border-radius: 10px;
    background-color: $gray-background;
    padding-bottom: 53px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;

    &--dark {
        border-color: $jet;
        background-color: $charcoal;
        color: $gray-background;
    }

    &__loading {
        position: absolute;
        top: 25%;
        transform: translateY(-50%);
    }

    &__question {
        &--correct{
            :deep(.uikit-question-choices-container__choice-container--correct) {
                &::after {
                    content: '';
                    border: 2px solid $cadaverous;
                    animation: fade-it .75s ease-in-out;

                    @media (prefers-reduced-motion: reduce) {
                        animation: none;
                    }

                    @keyframes fade-it {
                        0%  { box-shadow: 0px 0px 2px 2px rgba($cadaverous, 0.8); }
                        25%  { box-shadow: 0px 0px 4px 3px rgba($cadaverous, 0.6); }
                        50%  { box-shadow: 0px 0px 6px 5px rgba($cadaverous, 0.5); }
                        75%  { box-shadow: 0px 0px 8px 7px rgba($cadaverous, 0.4); }
                        90%  { box-shadow: 0px 0px 10px 8px rgba($cadaverous, 0.3); }
                        100%  { box-shadow: 0px 0px 11px 10px rgba($cadaverous, 0.2); }
                    }
                }
            }
        }
    }

    &__toolbar {
        position: absolute;
        bottom: 0;
        background-color: $pickled-bluewood;
        border-radius: 0 0 6px 6px;
        width: 100%;
        height: 53px;
        display: flex;
        align-items: center;
        justify-content: space-around;
        color: $white;
    }

    &__flag-toggle {
        position: relative;
        outline: none;
        display: flex;

        &:focus::before {
            content: '';
            position: absolute;
            border: 1px solid $butterscotch;
            width: 32px;
            height: 32px;
            top: 8px;
            left: 8px;
            border-radius: 4px;
            box-sizing: border-box;
        }

        @media (hover: hover) {
            .qotd-container__flag-icon:hover {
                color: $banana-bread;
            }
        }

        .qotd-container__flag-icon {
            cursor: pointer;

            &--active {
                color: $banana-bread;
            }
        }

        .uikit-tooltip {
            position: absolute;
            left: 50%;
            top: calc(-100% + 13px);
        }

        &--disabled {
            opacity: 0.35;

            .qotd-container__flag-icon {
                cursor: default;

                @media (hover: hover) {
                    &:hover {
                        color: $white;
                    }
                }
            }
        }
    }

    &__people-icon,
    &__share-icon {
        position: relative;
        cursor: pointer;
        height: 30px;
        display: flex;
        align-items: center;

        &:hover {
            color: $banana-bread;
        }

        &--active {
            color: $banana-bread;
        }

        svg:focus {
            outline: none;
            color: $banana-bread;
        }

        .uikit-tooltip {
            position: absolute;
            left: 50%;
            top: calc(-100% - 14px);
        }
    }

    &__people-icon {
        &--disabled {
            cursor: default;

            &:hover {
                color: $white;
            }
        }
    }

    &__share-icon {
        &--disabled {
            cursor: default;
            opacity: 0.35;

            &:hover {
                color: $white;
            }
        }
    }
}

</style>
